// @flow
import React from 'react'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { FeedbackMessage } from '@ta-interaktiv/react-feedback-message'

import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/rail.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/icon.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/button.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/message.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import 'styles/App.css'
import './styles.scss'

import Map from '../map/container'
import { LoadingIndicator } from '../loadingIndicator/index'
import { Selector } from '../selector/index'
import MunicipalityDetail from 'components/municipalityDetail'
import InteractionCapabilities from 'components/interactionCapabilities'
import Popup from 'components/popup'
import { ErrorBoundary } from 'components/errorBoundary'
import { ArticleTeasers } from '@ta-interaktiv/react-article-teasers'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import Waypoint from 'react-waypoint'
import { mapActiveMunicipality } from 'components/map/reducer'
import { connect } from 'react-redux'
import Scrollchor from 'react-scrollchor'
import { Legend } from 'components/legend'
import { withRail } from 'components/rail'
import { RenterProfile } from 'components/renterProfile'
import { Footer } from '@ta-interaktiv/react-footer'

type Props = {
  articleId: number,
  mouseOverHandler: (number, SyntheticEvent<HTMLElement>) => void
}

type State = {
  publishedTimestamp: number
}
/**
 * Show a block for jumping directly to the Zurich part
 */
const jumpMessage = () => (
  <div className='jump-message'>
    <div className='ui info top attached message'>
      Sie interessieren sich für den Kanton und die Stadt Zürich?
    </div>
    <Scrollchor to='zurich' className='ui primary fluid bottom attached button'>
      <i className='down arrow icon' />Direkt zu Zürich springen
    </Scrollchor>
  </div>
)

const articleList = () => (
  <aside className='ui green segment'>
    <div className='ui icon very relaxed infographic attached link list'>
      <a
        href={`https://www.tagesanzeiger.ch/30089810`}
        target='_parent'
        className='item'
      >
        <i className='external icon bottom aligned' />
        <div className='content'>
          Leben am Rande des Kantons
          <div className='header'>Zum Beispiel Hagenbuch</div>
        </div>
      </a>
    </div>
  </aside>
)

const RailedJumpMessage = withRail(jumpMessage)
// eslint-disable-next-line no-unused-vars
const RailedArticleList = withRail(articleList)

export class AppComponent extends React.Component<Props, State> {
  static defaultProps = {
    articleId: 20136903
  }

  render () {
    return (
      <div className='index'>
        <Masthead
          articleId={this.props.articleId}
          additionalFacebookShares={140}
          additionalShares={31 + 106 + 235}
        />
        <ThemeSwitch />
        <InteractionCapabilities />
        <header className='ui vertical segment'>
          <div className='ui container'>
            <h1 className='ui header' style={{ marginBottom: 0 }}>
              Wo Sie die Miete noch zahlen können
            </h1>
            <h2 className='ui header' style={{ marginTop: 0 }}>
              <div className='small sub header'>
                Wir haben rund eine Million Wohnungsinserate visualisiert.
                Unsere interaktive Karte zeigt, in welchen Gemeinden Sie sich
                mit Ihrem Budget eine Bleibe noch leisten können.
              </div>
            </h2>
            <p className='ta author'>
              Von Kaspar Manz und Marc Brupbacher (Interaktiv-Team) und Martin
              Sturzenegger (Text)
            </p>
          </div>
        </header>

        <Waypoint
          scrollableAncestor={window}
          onLeave={({ previousPosition, currentPosition }) => {
            if (
              previousPosition === Waypoint.inside &&
              currentPosition === Waypoint.above
            ) {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: 'startReading'
              })
            }
          }}
        />

        <main className='ui vertical segment'>
          <div className='ui text container'>
            <RailedJumpMessage side='right' />
            <p>
              Kann ich eine Dreizimmerwohnung in der Stadt Zürich noch zahlen?
              Wo muss ich mit meinem Budget hinziehen, damit jeder in der
              Familie sein eigenes Zimmer bekommt? Unsere interaktive Karte
              zeigt es. Das Interaktiv-Team des «Tages-Anzeigers» hat 923’946
              Wohnungsinserate visualisiert.{' '}
              <strong>
                Die Nebenkosten sind in den angegebenen Preisen nicht enthalten.
              </strong>{' '}
              Erhoben hat die Daten das Immobilienberatungsbüro Wüest Partner
              von Anfang 2016 bis Mitte 2017.{' '}
              <a href='#methodik'>
                Mehr zu den Daten finden Sie am Ende des Beitrags.
              </a>
            </p>
          </div>
        </main>

        <aside
          className='ui vertical green segment switzerland-map'
          onMouseOver={event => this.props.mouseOverHandler(-1, event)}
        >
          <div className='ui container'>
            <h3 className='ui infographic header'>
              Was suchen Sie für eine Wohnung?
            </h3>
            <Selector />
          </div>
          <div style={{ position: 'relative' }}>
            <LoadingIndicator />{' '}
            <ErrorBoundary>
              <Legend />
              <div className='' style={{ overflow: 'hidden' }}>
                <Map />
              </div>
            </ErrorBoundary>
          </div>
        </aside>

        <Waypoint
          scrollableAncestor={window}
          onEnter={({ previousPosition, currentPosition }) => {
            if (
              previousPosition === Waypoint.below &&
              currentPosition === Waypoint.inside
            ) {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: '33ContentRead'
              })
            }
          }}
        />

        <main className='ui vertical segment'>
          <div className='ui text container' style={{ position: 'relative' }}>
            <FeedbackMessage />
            <p>
              Die Mieten sind vor allem in den Städten Zürich, Genf und Lausanne
              sowie in deren Vororten exorbitant. In Mies (VD), Commugny (VD),
              Tannay (VD) und Pregny-Chambésy (GE) gab es nur 5 Prozent der
              Vierzimmerwohnungen für weniger als 2500.– Franken. Ohne
              Nebenkosten. In der Stadt Genf zahlte man für 1987 von 5254
              (37,8%) ausgeschriebenen Vierzimmerwohnungen weniger als 2500.–
              Franken. In der Stadt Zürich waren von Anfang 2016 bis Mitte 2017
              insgesamt 7655 Vierzimmerwohnungen ausgeschrieben. Lediglich 39
              Prozent gab es unter 2500 Franken. Besser sah es in der Stadt Bern
              aus: 79,9 Prozent der Vierzimmerwohnungen gab es für weniger als
              2500 Franken, in Luzern 74,6 Prozent, in Basel 74 Prozent. Am
              allergünstigsten ist das Wohnen hierzulande im Kanton Jura und im
              Berner Jura, wo man in etlichen kleineren Gemeinden sehr gute
              Chancen hat, für weniger als 1000 Franken pro Monat eine 4- bis
              4,5-Zimmer-Wohnung zu ergattern.
            </p>
          </div>
        </main>

        <aside
          className='ui vertical green segment zurich-map'
          id='zurich'
          onMouseOver={event => this.props.mouseOverHandler(-1, event)}
        >
          <div className='ui container'>
            <h3 className='ui infographic header'>
              Wie sieht es in Zürich aus?
            </h3>
            <Selector />

            <ErrorBoundary>
              <div style={{ position: 'relative' }}>
                <div className='mobile-block'>
                  <LoadingIndicator />
                  <Legend />
                </div>

                <div
                  className='ui text container'
                  style={{ overflow: 'hidden', position: 'relative' }}
                >
                  {
                    // This is the map using a filter in action. In this
                    // case, the map only shows municipalities that belong
                    // to the «Grossregion» (property: "GRNR") Zürich (id: 4).
                    // For the canton of Berne, this would be {property:
                    // 'KTNR', id: 2}
                    // You might have to adapt the containerHeightRatio to
                    // match the width/height of the desired area
                  }
                  <Map
                    filter={{
                      property: 'GRNR',
                      id: 4
                    }}
                    containerHeightRatio={1.2}
                    patternScale={2}
                  />
                </div>
              </div>
            </ErrorBoundary>
          </div>
        </aside>

        <main className='ui vertical segment'>
          <div className='ui text container'>
            <p>
              Die Wahl des Wohnorts ist kein Wunschkonzert. Das gilt vor allem
              für Wohnungssuchende im Raum Zürich. Seit Jahrzehnten sehen sie
              sich mit steigenden Mieten konfrontiert. Die hohen Mieten sind
              Ausdruck des wirtschaftlichen Erfolgs. Zürichs Wirtschaftsmotor
              brummt, während es immer mehr Menschen schwerfällt, bezahlbaren
              Wohnraum zu finden. Wer nicht von einem subventionierten Angebot
              profitiert, bewegt sich auf dem freien Markt. Alleinerziehende,
              Arbeitslose, Studierende und immer mehr auch Vertreter des
              Mittelstands: sie müssen sich fragen, ob es für sie in diesem
              Kanton noch ein Plätzchen gibt. Falls ja: Wo befindet es sich?
              Alle halten sich bei der Suche ihres Wunschobjekts an die eiserne
              Regel: Die Monatsmiete beträgt nicht mehr als ein Drittel des
              Bruttoverdienstes. Wir haben fünf Szenarien durchgespielt.
            </p>
          </div>
        </main>

        <main className='ui vertical segment'>
          <div className='ui three column doubling stackable centered relaxed grid container'>
            <RenterProfile
              desiredLocation='mind. 3-Zimmer-Wohnung in der Stadt Zürich'
              realisticChoice='Winterthur'
              salary='4800.– (inkl. Unterhaltszahlung und Kinderzulagen)'
              title='Die Alleinerziehende'
              upperLimit='1600.–'
               illustrationSrc={require('./alleinerziehend.svg')}
            >
              <p>
                Sie gelten als die stillen Kämpferinnen und Kämpfer unserer
                Gesellschaft: die Alleinerziehenden. Über 90 Prozent gehen einem
                Job nach, daneben gilt es die Kinder zu versorgen. Zum Klagen
                bleibt ihnen kaum Zeit, so auch Barbara. Nach der Trennung von
                ihrem Mann sucht sie für sich und ihre beiden Kinder eine neue
                Bleibe. Ihr privates Umfeld lebt in Zürich, sie schätzt ein
                breites kulturelles Angebot. Deshalb hat eine zentrale Lage mit
                gutem Kinderkrippenangebot Vorrang. Barbara wäre bereit, den
                Stadtkreis zu wechseln, ein Wechsel in die Agglomeration kommt
                für sie nicht infrage.
              </p>
              <p>
                Nach kurzer Suche schwinden bei Barbara die Hoffnungen, in der
                Stadt ein bezahlbares Objekt zu finden. In den Kreisen 4 und 5
                liegen nur 7,6 Prozent beziehungsweise 6,2 Prozent der
                3-Zimmer-Wohnungen im Budget. Die beste Ausgangslage bietet sich
                für die Alleinerziehende noch im Kreis 12 (Schwamendingen) mit
                40,5 Prozent. Weil Barbara aber ein belebtes Stadtleben wichtig
                ist, richtet sie ihren Fokus auf Winterthur: Knapp die Hälfte
                der 5414 Wohnungsinserate sind innerhalb ihres Budgets.
              </p>
            </RenterProfile>

            <RenterProfile
              desiredLocation='4 Zimmer, zentral in Zürich'
              realisticChoice='Altstetten'
              salary='6000.–'
              title='Die Studi-WG'
              upperLimit='2000.–'
               illustrationSrc={require('./wg.svg')}
            >
              <p>
                Bessere Chancen auf eine bezahlbare Wohnung in einem zentralen
                Zürcher Stadtkreis haben die drei Studenten Robin, Tom und
                Paula. Inklusive den Zustüpfen ihrer Eltern bringen sie es auf
                ein monatliches Gehalt von 6000 Franken. Alle drei Studieren in
                Zürich, alle wünschen sich eine Wohnung, die in der Nähe des
                Zürcher Nachtlebens liegt.
              </p>
              <p>
                Fast im ganzen Kanton haben die drei sehr gute Chancen eine
                bezahlbare Wohnung zu finden. Doch ausgerechnet in Zürich sieht
                es düster aus: Im bevorzugten Kreis 5 stehen die Chancen bei
                gerade mal 6,8 Prozent. Im benachbarten und nicht minder hippen
                Kreis 4 steigt die Wahrscheinlichkeit auf immerhin 17,2 Prozent.
                Das ist besser als beispielsweise in Wipkingen (Paulas Favorit)
                mit 11,2 Prozent bezahlbaren Wohnungen. Weil die Zeit drängt und
                Tom hörte, dass in Altstetten nun auch Pop-up-Restaurants
                eröffnen, erweitern sie ihr Suchgebiet auf den Kreis 9. Der
                Prozentsatz ist mit 16,7 zwar leicht tiefer als im Kreis 4. Doch
                das Angebot ist mit 111 verfügbaren Objekten deutlich breiter.
              </p>
            </RenterProfile>

            <RenterProfile
              desiredLocation='4,5 Zimmer im Grünen, tiefer Steuerfuss'
              realisticChoice='Greifensee'
              salary='9000.–'
              title='Die Familienplaner'
              upperLimit='2000.–'
               illustrationSrc={require('./familienplaner.svg')}
            >
              <p>
                Das junge Paar Claudia und Anton plant seine Zukunft. Die
                Wohnung soll in einer ländlichen Gemeinde liegen und Platz für
                zwei Kinder bieten. Weil beide in Zürich arbeiten, sind sie auf
                einen guten verkehrstechnischen Anschluss angewiesen. Das Budget
                legen sie bei 2000.– fest, weil Claudia im Kindesfall nicht
                arbeiten will.
              </p>
              <p>
                Weil die Stadt für Claudia und Anton ohnehin nicht infrage
                kommt, stehen ihre Chancen gut. In Uster, einer ihrer
                Favoritengemeinden, liegen 60,2 Prozent der Wohnungen im
                bezahlbaren Bereich. Nach weiterer Suche stossen die beiden
                jedoch auf einen Geheimtipp: In Greifensee, nur 20 Zugminuten
                von Zürich entfernt, liegen 256 der 277 4-Zimmer-Wohnungen bei
                unter 2000 Franken Miete. Zudem bietet die Gemeinde einen
                überdurchschnittlich tiefen Steuerfuss von 91 Prozent.
              </p>
            </RenterProfile>

            <RenterProfile
              desiredLocation='1,5 Zimmer, irgendwo in der Stadt Zürich'
              realisticChoice='Schwamendingen'
              salary='–'
              title='Der Sozialhilfebezüger'
              upperLimit='1100.–'
               illustrationSrc={require('./sozialhilfe.svg')}
            >
              <p>
                Als ausgesteuerter Sozialfall ist Richard auf Wohnungssuche.
                Weil sein privates Umfeld in der Stadt lebt, begrenzt der
                Alleinstehende seine Suche auf Zürich. Dafür stehen ihm gemäss
                Sozialhilfegesetz höchstens 1100 Franken für die Monatsmiete zu.
              </p>
              <p>
                Zürich ist für Richard ein hartes Pflaster. In allen
                Stadtkreisen liegt die Wahrscheinlichkeit deutlich unter 50
                Prozent, um eine bezahlbare 1,5-Zimmer-Wohnung zu finden.
                Bemerkenswerterweise wären seine Chancen in Rüschlikon, einer
                der reichsten Gemeinden der Schweiz, grösser. Immerhin 17 der 33
                Wohnungen lägen in Richards Budget. Dazu käme ein tiefer
                Steuerfuss von 78 Prozent. Da bei Richard Letzteres jedoch nicht
                ins Gewicht fällt und er in der Stadt bleiben will, konzentriert
                er sich auf Schwamendingen. Dort liegen 56,6 Prozent der
                Wohnungen im bezahlbaren Bereich.
              </p>
              <div className='ui icon infographic very relaxed celled blue list'>
                <a
                  href='https://www.tagesanzeiger.ch/30089810'
                  className='item'
                  target='_parent'
                >
                  <i className='external icon' />
                  <div className='content'>Mehr zu Schwamendingen</div>
                </a>
              </div>
            </RenterProfile>

            <RenterProfile
              desiredLocation='5-Zimmer-Wohnung ums Seebecken'
              realisticChoice='Oberrieden'
              salary='10000.–'
              title='Die verdrängten Nutzniesser'
              upperLimit='3000.–'
               illustrationSrc={require('./nutzniesser.svg')}
            >
              <p>
                Bernadette und Benedict leben zurzeit noch in ihrer
                5-Zimmer-Stadtwohnung in der Zürcher Altstadt für 2800.–. Die
                Kinder sind inzwischen ausgezogen, und die Stadt hat die
                Kriterien für subventioniertes Wohnen verschärft. Sprich:
                Bernadette und Benedict müssen raus. Auf die fünf Zimmer möchten
                sie allerdings nicht verzichten, in der Zürcher Altstadt fühlen
                sie sich wohl. Eine Gemeinde mit Seeanstoss wäre für sie die
                einzige Alternative. Dafür sind sie bereit, das Budget auf
                maximal 3000.– zu erhöhen.
              </p>
              <p>
                Zum ersten Mal seit langem mit dem freien Wohnungsmarkt
                konfrontiert, erschrickt das Paar: Nur eine von 57 Wohnungen im
                geliebten Zürcher Kreis 1 läge innerhalb ihres Budgets. Nach
                langem Suchen stossen sie jedoch auf eine unscheinbare Gemeinde
                am linken Zürichseeufer: Oberrieden. Dort sind immerhin 37 von
                93 5-Zimmer-Wohnungen für unter 3000 Franken verfügbar. Dazu
                kommt ein sehr tiefer Steuerfuss von 88 Prozent.
              </p>
              <div className='ui icon infographic very relaxed celled blue list'>
                <a
                  href='https://www.tagesanzeiger.ch/27804873'
                  className='item'
                  target='_parent'
                >
                  <i className='external icon' />
                  <div className='content'>Mehr zu Oberrieden</div>
                </a>
              </div>
            </RenterProfile>
          </div>
        </main>

        <Waypoint
          scrollableAncestor={window}
          onEnter={({ previousPosition, currentPosition }) => {
            if (
              previousPosition === Waypoint.below &&
              currentPosition === Waypoint.inside
            ) {
              window.dataLayer = window.dataLayer || []
              window.dataLayer.push({
                event: '60ContentRead'
              })
            }
          }}
        />

        <Footer
          articleId={this.props.articleId}
          title='Wie bewerten Sie diese interaktive Arbeit?'
          credits={[
            [
              'Idee, Konzept und Programmierung',
              'Kaspar Manz und Marc' + ' Brupbacher, Interaktiv-Team'
            ],
            ['Text', 'Martin Sturzenegger'],
            ['Illustrationen', 'Marina Bräm'],
            ['Datenquelle', 'Wüest Partner'],
            [
              'Inspiration',
              "<a href='https://interaktiv.morgenpost.de/mietkarte-berlin/'>Wo Sie sich Berlin noch leisten können</a>, «Berliner Morgenpost»"
            ]
          ]}
          questions={[
            'Wie informativ war der Beitrag?',
            'Wie angenehm war die Bedienung der interaktiven Elemente?',
            'Wie gefällt Ihnen die Gestaltung?'
          ]}
        >
          <main className='ui vertical segment'>
            <div className='ui container'>
              <ArticleTeasers
                articleIds={['15620677', '30089810', '27804873', '21249326']}
                numberOfCards='two'
                className='raised'
              />
            </div>
          </main>
          <main className='ui vertical attached segment'>
            <div className='ui text container'>
              <h2 className='ui small header' id='methodik'>
                Über die Daten
              </h2>
              <p>
                In der Schweiz kommt es jährlich zu rund 400’000 Mieterwechseln.
                Den Grossteil davon erfasst das Monitoring von Wüest Partner. Es
                wertet die Wohnungsangebote aller gängigen Internetportale aus
                und erfasst die Inserate in 120 Zeitungen und Zeitschriften.
                Daraus resultiert die grösste Mietpreis-Datensammlung der
                Schweiz.
              </p>

              <p>
                Doch die Vermieter schreiben nicht alle frei werdenden Wohnungen
                im Internet oder per Inserat aus. Verzichten sie darauf, fliesst
                der entsprechende Mietpreis nicht in die Datensammlung ein. Etwa
                bei einer Vergabe unter der Hand oder via Warteliste. Vor allem
                bei Genossenschaftswohnungen kommt dies oft vor. Generell werden
                günstige Wohnungen eher unter der Hand weitergereicht als teure
                Apartments. Die Angebotspreise liegen also im Mittel etwas
                tiefer als hier ausgewiesen. Die Zahlen von Wüest Partner
                zeigen, womit man rechnen muss, wenn man im Internet oder per
                Inserat eine Wohnung sucht. Dies trifft für 60 bis 80 Prozent
                aller Mieterwechsel zu. Zudem: Die visualisierten Mietpreise
                sind alle exklusive Nebenkosten.
              </p>

              <p>
                Die Auswertung erfolgte pro Quartal. Der Auswertungszeitraum
                erstreckt sich von Anfang 2016 bis Mitte 2017 (6 Quartale). Um
                Mehrfachnennungen innerhalb eines Quartals zu vermeiden, wurde
                eine Duplikatbereinigung vorgenommen. Jedes Objekt wird damit
                pro Quartal maximal einmal gezählt. Objekte, die die über
                mehrere Quartale ausgeschrieben waren, wurden mehrfach gezählt.
                Insgesamt wurden 923’946 Inserate erfasst.
              </p>
              <p>
                Für die Berechnung der Anteile wurden jeweils nur die Anzeigen
                herangezogen, deren Angaben komplett waren. In der
                Visualisierung kann es deshalb zu rundungsbedingten Diskrepanzen
                zwischen den absoluten Zahlen, die sich auf alle erfassten
                Inserate beziehen, und den Prozentangaben kommen.
              </p>
            </div>
          </main>
        </Footer>

        <Popup>
          <MunicipalityDetail />
        </Popup>
      </div>
    )
  }
}

function mapDispatchToProps (dispatch) {
  return {
    mouseOverHandler: (id: number, event: SyntheticEvent<HTMLElement>) => {
      dispatch(mapActiveMunicipality(id))
    }
  }
}

export default connect(null, mapDispatchToProps)(AppComponent)
